import { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '@/App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import { Heart, MessageCircle, Send, Image, Video, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { PostSkeleton } from '@/components/Skeleton';
import VideoPlayer from '@/components/VideoPlayer';
import CallAnalytics from '@/components/CallAnalytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, token } = useContext(AuthContext);
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedMedia, setUploadedMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPosts();
  }, [page]);

  const fetchPosts = async () => {
    setLoadingPosts(true);
    try {
      const response = await axios.get(`${API}/posts?page=${page}&limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (page === 1) {
        setPosts(response.data);
      } else {
        setPosts([...posts, ...response.data]);
      }
      setHasMore(response.data.length === 10);
    } catch (error) {
      toast.error('Failed to load posts');
    } finally {
      setLoadingPosts(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + selectedFiles.length > 5) {
      toast.error('Maximum 5 files allowed per post');
      return;
    }
    
    // Validate file sizes
    for (const file of files) {
      const isVideo = file.type.startsWith('video/');
      const maxSize = isVideo ? 50 * 1024 * 1024 : 5 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error(`${file.name} exceeds size limit (${isVideo ? '50MB' : '5MB'})`);
        return;
      }
    }
    
    setSelectedFiles([...selectedFiles, ...files]);
  };

  const removeFile = (index) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  const uploadMedia = async () => {
    if (selectedFiles.length === 0) return [];
    
    setUploading(true);
    const formData = new FormData();
    selectedFiles.forEach(file => {
      formData.append('files', file);
    });
    
    try {
      const response = await axios.post(`${API}/upload/media`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data.media;
    } catch (error) {
      toast.error('Failed to upload media');
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const createPost = async () => {
    if (!newPost.trim() && selectedFiles.length === 0) {
      toast.error('Please add some content or media');
      return;
    }
    
    setLoading(true);
    try {
      // Upload media first if any
      let media = [];
      if (selectedFiles.length > 0) {
        media = await uploadMedia();
      }
      
      await axios.post(
        `${API}/posts/create`,
        { content: newPost, media },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewPost('');
      setSelectedFiles([]);
      toast.success('Post created!');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to create post');
    } finally {
      setLoading(false);
    }
  };

  const likePost = async (postId) => {
    try {
      const response = await axios.post(
        `${API}/posts/${postId}/like`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPosts(posts.map(p => p.id === postId ? response.data : p));
    } catch (error) {
      toast.error('Failed to like post');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-peach-50 to-teal-50">
      <Navbar />
      
      <div className="max-w-3xl mx-auto px-4 py-8 pt-24">
        {/* Call Analytics */}
        <div className="mb-6">
          <CallAnalytics />
        </div>
        
        {/* Create Post */}
        <div className="glass-dark rounded-3xl p-6 mb-6 shadow-lg" data-testid="create-post-section">
          <h2 className="text-2xl font-bold mb-4">Share Your Story</h2>
          <div className="flex items-start space-x-4 mb-4">
            <Avatar className="border-2 border-teal">
              <AvatarImage src={user?.profile_photo} />
              <AvatarFallback className="bg-white text-black font-bold">
                {user?.name?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
            <Textarea
              data-testid="post-content-input"
              placeholder="What's on your mind?"
              value={newPost}
              onChange={(e) => setNewPost(e.target.value)}
              className="flex-1 rounded-xl min-h-[100px] resize-none border-gray-200"
            />
          </div>
          
          {/* File previews */}
          {selectedFiles.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 mb-4">
              {selectedFiles.map((file, index) => (
                <div key={`upload-${file.name}-${index}`} className="relative group">
                  <div className="aspect-square rounded-xl overflow-hidden bg-gray-200 flex items-center justify-center">
                    {file.type.startsWith('image/') ? (
                      <img 
                        src={URL.createObjectURL(file)} 
                        alt="preview" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Video className="w-12 h-12 text-gray-400" />
                    )}
                  </div>
                  <button
                    onClick={() => removeFile(index)}
                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <p className="text-xs text-gray-600 mt-1 truncate">{file.name}</p>
                </div>
              ))}
            </div>
          )}
          
          <div className="flex items-center justify-between">
            <div className="flex space-x-2">
              <input
                type="file"
                id="media-upload"
                accept="image/*,video/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
              <label
                htmlFor="media-upload"
                className="cursor-pointer inline-flex items-center px-4 py-2 rounded-full bg-white text-black border-2 border-teal hover:bg-teal hover:text-white font-semibold"
              >
                <Image className="w-4 h-4 mr-2" />
                Add Photos/Videos
              </label>
              {selectedFiles.length > 0 && (
                <span className="flex items-center text-sm text-gray-600">
                  {selectedFiles.length}/5 files
                </span>
              )}
            </div>
            <Button
              data-testid="create-post-btn"
              onClick={createPost}
              disabled={loading || uploading || (!newPost.trim() && selectedFiles.length === 0)}
              className="bg-white text-black border-2 border-teal hover:bg-teal hover:text-white rounded-full px-6 font-semibold"
            >
              <Send className="w-4 h-4 mr-2" />
              {uploading ? 'Uploading...' : 'Post'}
            </Button>
          </div>
        </div>

        {/* Posts Feed */}
        <div className="space-y-6" data-testid="posts-feed">
          {loadingPosts && page === 1 ? (
            // Show skeletons on initial load
            <>
              <PostSkeleton />
              <PostSkeleton />
              <PostSkeleton />
            </>
          ) : posts.length === 0 ? (
            <div className="glass rounded-3xl p-12 text-center">
              <p className="text-gray-500 text-lg">No posts yet. Be the first to share!</p>
            </div>
          ) : (
            <>
              {posts.map((post) => (
                <div
                  key={post.id}
                  data-testid={`post-${post.id}`}
                  className="glass-dark rounded-3xl p-6 shadow-lg card-hover"
                >
                  <div className="flex items-start space-x-4 mb-4">
                    <Avatar
                      className="cursor-pointer border-2 border-teal"
                      onClick={() => navigate(`/user/${post.user_id}`)}
                    >
                      <AvatarImage src={post.user_photo} loading="lazy" />
                      <AvatarFallback className="bg-white text-black font-bold">
                        {post.user_name.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <h3
                        className="font-semibold text-lg cursor-pointer hover:text-coral transition-colors"
                        onClick={() => navigate(`/user/${post.user_id}`)}
                      >
                        {post.user_name}
                      </h3>
                      <p className="text-gray-500 text-sm">
                        {new Date(post.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  <p className="text-gray-800 mb-4 whitespace-pre-wrap">{post.content}</p>

                  {/* Media Gallery */}
                  {post.media && post.media.length > 0 && (
                    <div className={`grid gap-2 mb-4 ${
                      post.media.length === 1 ? 'grid-cols-1' :
                      post.media.length === 2 ? 'grid-cols-2' :
                      post.media.length === 3 ? 'grid-cols-3' :
                      'grid-cols-2'
                    }`}>
                      {post.media.slice(0, 4).map((item, index) => (
                        <div key={item.url || `media-${post.id}-${index}`} className="relative group rounded-xl overflow-hidden bg-gray-200">
                          {item.resource_type === 'video' ? (
                            <VideoPlayer
                              src={item.url}
                              playbackUrl={item.playback_url}
                              poster={item.thumbnail_url}
                              thumbnail={item.thumbnail_url}
                              captionsUrl={item.captions_url}
                            />
                          ) : (
                            <img
                              src={item.url}
                              alt="post media"
                              className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                              onClick={() => window.open(item.url, '_blank')}
                              loading="lazy"
                            />
                          )}
                          {post.media.length > 4 && index === 3 && (
                            <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center">
                              <span className="text-white text-3xl font-bold">+{post.media.length - 4}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center space-x-6 pt-4 border-t border-gray-200">
                    <button
                      data-testid={`like-btn-${post.id}`}
                      onClick={() => likePost(post.id)}
                      className={`flex items-center space-x-2 transition-colors ${
                        post.likes.includes(user.id) ? 'text-coral' : 'text-gray-500 hover:text-coral'
                      }`}
                    >
                      <Heart className={`w-5 h-5 ${post.likes.includes(user.id) ? 'fill-current' : ''}`} />
                      <span className="font-medium">{post.likes.length}</span>
                    </button>
                    <button className="flex items-center space-x-2 text-gray-500 hover:text-teal transition-colors">
                      <MessageCircle className="w-5 h-5" />
                      <span className="font-medium">{post.comments.length}</span>
                    </button>
                  </div>
                </div>
              ))}
              
              {/* Load More Button */}
              {hasMore && (
                <div className="text-center">
                  <Button
                    onClick={() => setPage(page + 1)}
                    disabled={loadingPosts}
                    className="bg-gradient-to-r from-teal to-teal-light text-white rounded-full px-8"
                  >
                    {loadingPosts ? 'Loading...' : 'Load More Posts'}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;